import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/useAnalytics";
import {
  useCurrentUser,
  useProfile,
  useGroups,
  useUpdateProfile,
  useUploadAvatar,
  useDeleteAccount,
  ProfileData,
  ProfileUpdateError,
} from "@/hooks/useProfileQueries";
import { getSubgroups, type Subgroup } from "@/lib/api";
import { signOut } from "@/lib/api";
import { useNavigate } from "react-router-dom";

interface ProfileFormContentProps {
  onSuccess?: () => void;
}

export function ProfileFormContent({ onSuccess }: ProfileFormContentProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const { trackFeatureUsage } = useAnalytics();
  
  // React Query hooks
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const { data: profileData, isLoading: profileLoading, isFetching } = useProfile(user?.id);
  const { data: groups = [] } = useGroups();
  const [subgroups, setSubgroups] = useState<Subgroup[]>([]);
  const [availableSubgroups, setAvailableSubgroups] = useState<Subgroup[]>([]);
  const updateProfileMutation = useUpdateProfile();
  const uploadAvatarMutation = useUploadAvatar();
  const deleteAccountMutation = useDeleteAccount();

  // Local state for form
  const [profile, setProfile] = useState<ProfileData>({
    first_name: "",
    last_name: "",
    phone: "",
    country_code: "+1",
    street_address: "",
    street_address_line_2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    gender: "",
    date_of_birth: "",
    avatar_url: "",
    preferred_language: "",
    group_id: "",
    subgroup_id: "",
    primary_product_id: "",
    invite_code_used: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [synced, setSynced] = useState(false);

  // Check if group is locked (either via invite code or already assigned)
  // Once a group is assigned, it cannot be changed by the user
  const isGroupLocked = !!(
    user?.user_metadata?.signup_method === 'invite_code' ||
    profileData?.group_id
  );

  // Load subgroups
  useEffect(() => {
    async function loadSubgroups() {
      const result = await getSubgroups();
      if (result.success) setSubgroups(result.data);
    }
    loadSubgroups();
  }, []);

  // Filter available subgroups based on selected group
  useEffect(() => {
    if (profile.group_id) {
      setAvailableSubgroups(subgroups.filter(s => s.group_id === profile.group_id));
    } else {
      setAvailableSubgroups([]);
      if (profile.subgroup_id) {
        setProfile(prev => ({ ...prev, subgroup_id: '' }));
      }
    }
  }, [profile.group_id, subgroups]);

  // Sync profile data to local state when loaded
  useEffect(() => {
    if (profileData) {
      const metadata = user?.user_metadata as Record<string, unknown> | undefined;
      const groupFromMetadata = metadata?.signup_group_id as string | undefined;
      const effectiveGroupId = profileData.group_id || groupFromMetadata || '';
      
      setProfile({
        ...profileData,
        group_id: effectiveGroupId,
      });
      setSynced(true);
    }
  }, [profileData, user?.user_metadata]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!profile.first_name?.trim()) errors.first_name = t('common:profile.errors.firstNameRequired', 'First name is required');
    if (!profile.last_name?.trim()) errors.last_name = t('common:profile.errors.lastNameRequired', 'Last name is required');
    if (!profile.phone?.trim()) errors.phone = t('common:profile.errors.phoneRequired', 'Phone number is required');
    if (!profile.street_address?.trim()) errors.street_address = t('common:profile.errors.streetAddressRequired', 'Street address is required');
    if (!profile.city?.trim()) errors.city = t('common:profile.errors.cityRequired', 'City is required');
    if (!profile.state?.trim()) errors.state = t('common:profile.errors.stateRequired', 'State/Province is required');
    if (!profile.zip?.trim()) errors.zip = t('common:profile.errors.zipRequired', 'Zip/Postal code is required');
    if (!profile.country?.trim()) errors.country = t('common:profile.errors.countryRequired', 'Country is required');
    if (!profile.gender?.trim()) errors.gender = t('common:profile.errors.genderRequired', 'Gender is required');
    if (!profile.date_of_birth?.trim()) errors.date_of_birth = t('common:profile.errors.dateOfBirthRequired', 'Date of birth is required');
    if (!profile.preferred_language?.trim()) errors.preferred_language = t('common:profile.errors.languageRequired', 'Language is required');
    if (!profile.group_id?.trim()) errors.group_id = t('common:profile.errors.groupRequired', 'Group assignment is required');

    if (profile.date_of_birth) {
      const dob = new Date(profile.date_of_birth);
      const now = new Date();
      if (isNaN(dob.getTime())) {
        errors.date_of_birth = t('common:profile.errors.invalidDate', 'Invalid date format');
      } else if (dob > now) {
        errors.date_of_birth = t('common:profile.errors.futureDateOfBirth', 'Date of birth cannot be in the future');
      } else if (dob < new Date('1900-01-01')) {
        errors.date_of_birth = t('common:profile.errors.invalidDateOfBirth', 'Please enter a valid date of birth');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!validateForm()) {
      toast.error(t('common:profile.validationFailed', 'Please fill in all required fields'));
      return;
    }

    try {
      await updateProfileMutation.mutateAsync({ userId: user.id, profile });
      trackFeatureUsage('Profile Updated', { user_id: user.id });
      
      if (profile.preferred_language && profile.preferred_language !== i18n.language) {
        await i18n.changeLanguage(profile.preferred_language);
      }
      
      toast.success(t('common:profile.updateSuccess', 'Profile updated successfully'));
      setFormErrors({});
      onSuccess?.();
    } catch (error) {
      console.error('Profile update error:', error);
      
      if (error instanceof ProfileUpdateError && error.code === 'PHONE_DUPLICATE') {
        setFormErrors(prev => ({
          ...prev,
          phone: t('common:profile.errors.phoneDuplicate', 'This phone number is already registered to another account')
        }));
        toast.error(t('common:profile.errors.phoneDuplicate', 'This phone number is already registered to another account'));
        return;
      }
      
      console.error('Profile update error details:', error instanceof Error ? error.message : error);
      toast.error(t('common:profile.updateFailed', 'Failed to update profile'));
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('common:profile.avatarTooLarge', 'File size must be less than 2MB'));
      return;
    }

    try {
      const publicUrl = await uploadAvatarMutation.mutateAsync({ userId: user.id, file });
      setProfile({ ...profile, avatar_url: publicUrl });
      toast.success(t('common:profile.avatarSuccess', 'Avatar updated successfully'));
    } catch (_error) {
      toast.error(t('common:profile.avatarUploadFailed', 'Failed to upload avatar'));
    }
  };

  const _handleDeleteAccount = async () => {
    try {
      await deleteAccountMutation.mutateAsync();
      toast.success(t('common:profile.deleteSuccess', 'Account deleted successfully'));
      await signOut();
      navigate("/");
    } catch (_error) {
      toast.error(t('common:profile.deleteFailed', 'Failed to delete account'));
    }
  };

  const isUpdating = updateProfileMutation.isPending ||
                     uploadAvatarMutation.isPending ||
                     deleteAccountMutation.isPending;

  const RequiredLabel = ({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) => (
    <Label htmlFor={htmlFor}>
      {children} <span className="text-destructive">*</span>
    </Label>
  );

  const FieldError = ({ error }: { error?: string }) => (
    error ? <p className="text-sm text-destructive mt-1">{error}</p> : null
  );

  if (userLoading || profileLoading || isFetching || !synced) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <form onSubmit={handleProfileUpdate} className="space-y-6">
      {/* Avatar Section */}
      <div className="flex flex-col items-center gap-4">
        <Avatar className="h-20 w-20">
          <AvatarImage key={profile.avatar_url} src={profile.avatar_url} />
          <AvatarFallback>{profile.first_name?.[0] || user.email?.[0]}</AvatarFallback>
        </Avatar>
        <div className="flex gap-2">
          <Label htmlFor="avatar-upload-modal" className="cursor-pointer">
            <Button type="button" variant="outline" size="sm" asChild>
              <span>
                <Upload className="h-4 w-4 mr-2" />
                {t('profile.uploadPhoto', 'Upload Photo')}
              </span>
            </Button>
          </Label>
          <Input
            id="avatar-upload-modal"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarUpload}
            disabled={isUpdating}
          />
        </div>
      </div>

      <div className="grid gap-4">
        {/* Email (read-only) */}
        <div className="space-y-2">
          <Label htmlFor="email">{t('profile.email', 'Email')}</Label>
          <Input id="email" value={user.email} disabled />
        </div>

        {/* Name Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <RequiredLabel htmlFor="first_name">{t('profile.firstName', 'First Name')}</RequiredLabel>
            <Input
              id="first_name"
              value={profile.first_name}
              onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
              className={formErrors.first_name ? 'border-destructive' : ''}
            />
            <FieldError error={formErrors.first_name} />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="last_name">{t('profile.lastName', 'Last Name')}</RequiredLabel>
            <Input
              id="last_name"
              value={profile.last_name}
              onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
              className={formErrors.last_name ? 'border-destructive' : ''}
            />
            <FieldError error={formErrors.last_name} />
          </div>
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <RequiredLabel htmlFor="phone">{t('profile.phone', 'Phone')}</RequiredLabel>
          <PhoneInput
            countryCode={profile.country_code}
            phoneNumber={profile.phone}
            onCountryCodeChange={(value) => setProfile({ ...profile, country_code: value })}
            onPhoneNumberChange={(value) => {
              setProfile({ ...profile, phone: value });
              if (formErrors.phone) {
                setFormErrors(prev => ({ ...prev, phone: '' }));
              }
            }}
            disabled={isUpdating}
            error={!!formErrors.phone}
          />
          <FieldError error={formErrors.phone} />
        </div>

        {/* Address */}
        <div className="space-y-2">
          <RequiredLabel htmlFor="street_address">{t('profile.streetAddress', 'Street Address')}</RequiredLabel>
          <Input
            id="street_address"
            value={profile.street_address}
            onChange={(e) => setProfile({ ...profile, street_address: e.target.value })}
            className={formErrors.street_address ? 'border-destructive' : ''}
          />
          <FieldError error={formErrors.street_address} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="street_address_line_2">{t('profile.streetAddressLine2', 'Street Address Line 2')}</Label>
          <Input
            id="street_address_line_2"
            placeholder={t('profile.streetAddressLine2Placeholder', 'Apartment, suite, etc. (optional)')}
            value={profile.street_address_line_2}
            onChange={(e) => setProfile({ ...profile, street_address_line_2: e.target.value })}
          />
        </div>

        {/* City, State, Zip, Country */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="space-y-2">
            <RequiredLabel htmlFor="city">{t('profile.city', 'City')}</RequiredLabel>
            <Input
              id="city"
              value={profile.city}
              onChange={(e) => setProfile({ ...profile, city: e.target.value })}
              className={formErrors.city ? 'border-destructive' : ''}
            />
            <FieldError error={formErrors.city} />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="state">{t('profile.state', 'State')}</RequiredLabel>
            <Input
              id="state"
              value={profile.state}
              onChange={(e) => setProfile({ ...profile, state: e.target.value })}
              className={formErrors.state ? 'border-destructive' : ''}
            />
            <FieldError error={formErrors.state} />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="zip">{t('profile.zipCode', 'Zip Code')}</RequiredLabel>
            <Input
              id="zip"
              value={profile.zip}
              onChange={(e) => setProfile({ ...profile, zip: e.target.value })}
              className={formErrors.zip ? 'border-destructive' : ''}
            />
            <FieldError error={formErrors.zip} />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="country">{t('profile.country', 'Country')}</RequiredLabel>
            <Select value={profile.country || ""} onValueChange={(value) => setProfile({ ...profile, country: value })}>
              <SelectTrigger className={formErrors.country ? 'border-destructive' : ''}>
                <SelectValue placeholder={t('profile.selectCountry', 'Select')} />
              </SelectTrigger>
              <SelectContent className="bg-background z-50 max-h-[300px]">
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="CA">Canada</SelectItem>
                <SelectItem value="GB">United Kingdom</SelectItem>
                <SelectItem value="AU">Australia</SelectItem>
                <SelectItem value="NZ">New Zealand</SelectItem>
                <SelectItem value="IE">Ireland</SelectItem>
                <SelectItem value="DE">Germany</SelectItem>
                <SelectItem value="FR">France</SelectItem>
                <SelectItem value="IT">Italy</SelectItem>
                <SelectItem value="ES">Spain</SelectItem>
                <SelectItem value="PT">Portugal</SelectItem>
                <SelectItem value="NL">Netherlands</SelectItem>
                <SelectItem value="BE">Belgium</SelectItem>
                <SelectItem value="CH">Switzerland</SelectItem>
                <SelectItem value="AT">Austria</SelectItem>
                <SelectItem value="SE">Sweden</SelectItem>
                <SelectItem value="NO">Norway</SelectItem>
                <SelectItem value="DK">Denmark</SelectItem>
                <SelectItem value="FI">Finland</SelectItem>
                <SelectItem value="PL">Poland</SelectItem>
                <SelectItem value="CZ">Czech Republic</SelectItem>
                <SelectItem value="GR">Greece</SelectItem>
                <SelectItem value="JP">Japan</SelectItem>
                <SelectItem value="CN">China</SelectItem>
                <SelectItem value="IN">India</SelectItem>
                <SelectItem value="KR">South Korea</SelectItem>
                <SelectItem value="SG">Singapore</SelectItem>
                <SelectItem value="MY">Malaysia</SelectItem>
                <SelectItem value="TH">Thailand</SelectItem>
                <SelectItem value="PH">Philippines</SelectItem>
                <SelectItem value="ID">Indonesia</SelectItem>
                <SelectItem value="VN">Vietnam</SelectItem>
                <SelectItem value="BR">Brazil</SelectItem>
                <SelectItem value="MX">Mexico</SelectItem>
                <SelectItem value="AR">Argentina</SelectItem>
                <SelectItem value="CL">Chile</SelectItem>
                <SelectItem value="CO">Colombia</SelectItem>
                <SelectItem value="ZA">South Africa</SelectItem>
                <SelectItem value="EG">Egypt</SelectItem>
                <SelectItem value="NG">Nigeria</SelectItem>
                <SelectItem value="KE">Kenya</SelectItem>
                <SelectItem value="AE">United Arab Emirates</SelectItem>
                <SelectItem value="SA">Saudi Arabia</SelectItem>
                <SelectItem value="IL">Israel</SelectItem>
                <SelectItem value="TR">Turkey</SelectItem>
                <SelectItem value="RU">Russia</SelectItem>
                <SelectItem value="UA">Ukraine</SelectItem>
                <SelectItem value="RO">Romania</SelectItem>
                <SelectItem value="HU">Hungary</SelectItem>
              </SelectContent>
            </Select>
            <FieldError error={formErrors.country} />
          </div>
        </div>

        {/* Gender */}
        <div className="space-y-2">
          <RequiredLabel htmlFor="gender">{t('profile.genderLabel', 'Gender')}</RequiredLabel>
          <Select value={profile.gender || ""} onValueChange={(value) => setProfile({ ...profile, gender: value })}>
            <SelectTrigger className={formErrors.gender ? 'border-destructive' : ''}>
              <SelectValue placeholder={t('profile.selectGender', 'Select gender')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">{t('profile.gender.male', 'Male')}</SelectItem>
              <SelectItem value="female">{t('profile.gender.female', 'Female')}</SelectItem>
              <SelectItem value="other">{t('profile.gender.other', 'Other')}</SelectItem>
              <SelectItem value="prefer_not_to_say">{t('profile.gender.preferNot', 'Prefer not to say')}</SelectItem>
            </SelectContent>
          </Select>
          <FieldError error={formErrors.gender} />
        </div>

        {/* Date of Birth */}
        <div className="space-y-2">
          <RequiredLabel htmlFor="date_of_birth">{t('profile.dateOfBirth', 'Date of Birth')}</RequiredLabel>
          <Input
            id="date_of_birth"
            type="date"
            value={profile.date_of_birth}
            onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })}
            className={formErrors.date_of_birth ? 'border-destructive' : ''}
          />
          <FieldError error={formErrors.date_of_birth} />
        </div>

        {/* Preferred Language */}
        <div className="space-y-2">
          <RequiredLabel htmlFor="preferred_language">{t('profile.preferredLanguage', 'Preferred Language')}</RequiredLabel>
          <Select value={profile.preferred_language || ""} onValueChange={(value) => setProfile({ ...profile, preferred_language: value })}>
            <SelectTrigger className={formErrors.preferred_language ? 'border-destructive' : ''}>
              <SelectValue placeholder={t('profile.selectLanguage', 'Select language')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">🇬🇧 English</SelectItem>
              <SelectItem value="es">🇪🇸 Español</SelectItem>
              <SelectItem value="fr">🇫🇷 Français</SelectItem>
              <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
              <SelectItem value="pt">🇵🇹 Português</SelectItem>
              <SelectItem value="it">🇮🇹 Italiano</SelectItem>
            </SelectContent>
          </Select>
          <FieldError error={formErrors.preferred_language} />
        </div>

        {/* Group */}
        <div className="space-y-2">
          <RequiredLabel htmlFor="group_id">{t('profile.group', 'Group')}</RequiredLabel>
          {isGroupLocked ? (
            <>
              <div className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm opacity-60">
                {groups.find(g => g.id === profile.group_id)?.name || t('profile.noGroup', 'No group selected')}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('profile.groupLocked', 'Group was assigned via invite code and cannot be changed')}
              </p>
            </>
          ) : (
            <>
              <Select
                value={profile.group_id || ""}
                onValueChange={(value) => setProfile({ ...profile, group_id: value, subgroup_id: '' })}
              >
                <SelectTrigger className={formErrors.group_id ? 'border-destructive' : ''}>
                  <SelectValue placeholder={t('profile.noGroup', 'No group selected')}>
                    {profile.group_id 
                      ? groups.find(g => g.id === profile.group_id)?.name || t('profile.loading', 'Loading...')
                      : t('profile.noGroup', 'No group selected')}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-background z-50 max-h-[300px]">
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError error={formErrors.group_id} />
            </>
          )}
        </div>

        {/* Subgroup */}
        {profile.group_id && availableSubgroups.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="subgroup_id">{t('profile.subgroup', 'Subgroup')}</Label>
            <Select
              value={profile.subgroup_id || "none"}
              onValueChange={(value) => setProfile({ ...profile, subgroup_id: value === 'none' ? '' : value })}
              disabled={isGroupLocked}
            >
              <SelectTrigger className={isGroupLocked ? "opacity-60" : ""}>
                <SelectValue placeholder={t('profile.noSubgroup', 'No subgroup selected')} />
              </SelectTrigger>
              <SelectContent className="bg-background z-50 max-h-[300px]">
                <SelectItem value="none">{t('profile.noSubgroup', 'No subgroup')}</SelectItem>
                {availableSubgroups.map((subgroup) => (
                  <SelectItem key={subgroup.id} value={subgroup.id}>
                    {subgroup.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Invite Code Used */}
        {profile.invite_code_used && (
          <div className="space-y-2">
            <Label>{t('profile.inviteCodeUsed', 'Invite Code Used')}</Label>
            <Input
              value={profile.invite_code_used}
              disabled
              className="bg-muted text-muted-foreground cursor-not-allowed"
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button type="submit" disabled={isUpdating}>
          {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {t('profile.saveChanges', 'Save Changes')}
        </Button>
      </div>
    </form>
  );
}
